import { useContext } from 'react';
import ServicesContext from '../../global/services/ServicesContext';
import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { mutateDocumentsKeys } from './mutateKeyFactory';
import { LockUnlockDocumentsInfoRequest } from '../../Model/requests/LockUnlockDocumentsInfoRequest';
import { LockUnlockResponse } from '../../Model/responseDtos/LockUnlockResponse';
import { v4 as uuidv4 } from 'uuid';
import { useNotificationContext } from '../../global/hooks/useNotificationContext';
import { useSearchContext } from '../search/SearchContext';
import { searchKeys } from '../search/queryKeyFactory';
import { useGlobalizationContext } from '../globalization/GlobalizationContext';
import { useTranslation } from '../globalization/useTranslation';

export function useLockDocuments(): UseMutationResult<
  LockUnlockResponse,
  unknown,
  { dto: LockUnlockDocumentsInfoRequest }
> {
  const servicesCtx = useContext(ServicesContext);
  const { addCenterNotification } = useNotificationContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const queryClient = useQueryClient();
  const { queryRequest } = useSearchContext();

  const lockDocuments = (data: { dto: LockUnlockDocumentsInfoRequest }): Promise<LockUnlockResponse> => {
    if (servicesCtx?.documentsService && data.dto) {
      return servicesCtx.documentsService?.lockDocuments(data.dto);
    }
    throw new Error('Services context not initialized');
  };

  return useMutation({
    mutationKey: mutateDocumentsKeys.lockDocuments,
    mutationFn: (data: { dto: LockUnlockDocumentsInfoRequest }) => lockDocuments(data),
    onSuccess: async data => {
      addCenterNotification({
        id: uuidv4(),
        title: translate('custom.notifications.notificationCenter.lockDocument.lockDocumentSuccessTitle'),
        message: translate('custom.notifications.notificationCenter.lockDocument.lockDocumentSuccessMessage', {
          changed: data.changed.length,
          total: data.changed.length + data.skipped.length
        }),
        timestamp: new Date(),
        type: 'success'
      });

      if (queryRequest) {
        await queryClient.invalidateQueries({ queryKey: searchKeys.filteredSearch(queryRequest) });
      }
    },
    onError: () => {
      addCenterNotification({
        id: uuidv4(),
        title: translate('custom.notifications.notificationCenter.lockDocument.lockDocumentFailedTitle'),
        message: translate('custom.notifications.notificationCenter.lockDocument.lockDocumentFailedMessage'),
        timestamp: new Date(),
        type: 'error'
      });
    }
  });
}
